// NPM Imports
import typia from "typia";
// Local Imports
import { Result } from "./result";

//
// # Validator
// Parsing and validation from arbitrary JSON objects.
//
// Typical usage like so:
// ```
// interface X { ... }
// const X = {
//   validator: new Validator(typia.createValidate<X>()),
// };
// const parseResult = X.validator.parse(`some_string`);
// const validationResult = X.validator.validate({some: object});
// ```
//
// This is a bit more complicated than we'd like, since
// - (a) The `typia` library seems to catch errors in validation, but not JSON parsing in the first place!
// - (b) It also really does not want calls to any of its generic functions with generic parameters.
//   - Given how typia works (as a compiler plug-in), we get it wants type literals, I guess.
// So, each call site for `Validator()` needs to call `typia.createValidate<X>()` first.
//
export class Validator<T> {
  // Constructor requires the inner `typia` validator,
  // largely bc it needs to be created with a type literal.
  constructor(private inner: InnerValidator<T>) {}

  // Validate from JSON object
  validate = (input: unknown): Result<T> => {
    const validation = this.inner(input);
    if (validation.success) return Result.Ok(validation.data);
    return Result.Err(validation.errors);
  };

  // Parse from JSON string
  parse = (input: string): Result<T> => {
    // Step 1 : JSON parsing
    let parsed: unknown;
    try {
      parsed = JSON.parse(input);
    } catch (e) {
      return Result.Err(e);
    }
    // Step 2: type validation
    return this.validate(parsed);
  };
}

// Type alias for the functions returned by `typia.createValidate<T>()`
// NOTE: typia seems to freak out if this is not exported? ¯\_(ツ)_/¯
export type InnerValidator<T> = (input: unknown) => typia.IValidation<T>;

// And export these in this namespace, since most `Validators` need a call to em
export { createValidate } from "typia";
